import { css } from '@linaria/core'

import * as colors from './colors'

export interface ThemeDefinition {
  // backgrounds
  backgroundDefault: string
  backgroundPopout: string
  backgroundSubtle: string
  backgroundBoxed: string
  backgroundBoxedHover: string
  backgroundOk: string
  backgroundWarning: string
  backgroundWarningHover: string
  backgroundPrimary: string
  backgroundPrimaryHover: string
  backgroundSecondaryHover: string
  backgroundFocus: string
  backgroundAccent: string
  backgroundAccentHover: string
  // lines
  lineDefault: string
  lineInput: string
  lineInputHover: string
  lineImportant: string
  lineImportantHover: string
  lineFocus: string
  // text
  textDefault: string
  textDimmed: string
  textDeselected: string
  textDisabled: string
  textPrimary: string
  textImportantDimmed: string
  textImportant: string
  textDanger: string
  textLogo: string
}

const defaultTheme: ThemeDefinition = {
  // backgrounds
  backgroundDefault: colors.white,
  backgroundPopout: colors.white,
  backgroundSubtle: colors.silver050,
  backgroundBoxed: colors.silver100,
  backgroundBoxedHover: colors.silver200,
  backgroundOk: colors.green700,
  backgroundWarning: colors.yellow500,
  backgroundWarningHover: colors.yellow600,
  backgroundPrimary: colors.anthracite800,
  backgroundPrimaryHover: colors.anthracite900,
  backgroundSecondaryHover: colors.anthracite050,
  backgroundFocus: colors.focusDefault,
  backgroundAccent: colors.red500,
  backgroundAccentHover: colors.red600,
  // lines
  lineDefault: colors.silver200,
  lineInput: colors.anthracite500,
  lineInputHover: colors.anthracite900,
  lineFocus: colors.blue300,
  lineImportant: colors.red500,
  lineImportantHover: colors.red700,
  // text
  textDefault: colors.anthracite800,
  textDimmed: colors.anthracite500,
  textDeselected: colors.silver500,
  textDisabled: colors.silver400,
  textPrimary: colors.white,
  textImportant: colors.red500,
  textImportantDimmed: colors.importantDimmed,
  textDanger: colors.red700,
  textLogo: colors.black,
}

const highContrastTheme: ThemeDefinition = {
  // backgrounds
  backgroundDefault: colors.white,
  backgroundPopout: colors.white,
  backgroundSubtle: colors.silver100,
  backgroundBoxed: colors.silver200,
  backgroundBoxedHover: colors.silver300,
  backgroundOk: colors.green700,
  backgroundWarning: colors.yellow500,
  backgroundWarningHover: colors.yellow600,
  backgroundPrimary: colors.anthracite800,
  backgroundPrimaryHover: colors.anthracite900,
  backgroundSecondaryHover: colors.anthracite100,
  backgroundFocus: colors.focusHigh,
  backgroundAccent: colors.red500,
  backgroundAccentHover: colors.red800,
  // lines
  lineDefault: colors.silver500,
  lineInput: colors.anthracite900,
  lineInputHover: colors.anthracite900,
  lineFocus: colors.blue700,
  lineImportant: colors.red500,
  lineImportantHover: colors.red700,
  // text
  textDefault: colors.black,
  textDimmed: colors.anthracite600,
  textDeselected: colors.anthracite800,
  textDisabled: colors.silver400,
  textPrimary: colors.white,
  textImportant: colors.red500,
  textImportantDimmed: colors.importantDimmed,
  textDanger: colors.red700,
  textLogo: colors.black,
}

const darkTheme: ThemeDefinition = {
  // backgrounds
  backgroundDefault: colors.black,
  backgroundPopout: colors.silver855,
  backgroundSubtle: colors.silver800,
  backgroundBoxed: colors.silver750,
  backgroundBoxedHover: colors.silver700,
  backgroundOk: colors.green700,
  backgroundWarning: colors.yellow500,
  backgroundWarningHover: colors.yellow600,
  backgroundPrimary: colors.anthracite400,
  backgroundPrimaryHover: colors.anthracite500,
  backgroundSecondaryHover: colors.anthracite700,
  backgroundFocus: colors.anthracite600,
  backgroundAccent: colors.red500,
  backgroundAccentHover: colors.red600,
  // lines
  lineDefault: colors.silver825,
  lineInput: colors.anthracite500,
  lineInputHover: colors.anthracite400,
  lineFocus: colors.blue300,
  lineImportant: colors.red500,
  lineImportantHover: colors.red700,
  // text
  textDefault: colors.white,
  textDimmed: colors.anthracite200,
  textDeselected: colors.silver500,
  textDisabled: colors.silver500,
  textPrimary: colors.white,
  textImportant: colors.red500,
  textImportantDimmed: colors.red800,
  textDanger: colors.red700,
  textLogo: colors.black,
} as const

export const themeColors = {
  backgroundDefault: 'var(--background-default)',
  backgroundPopout: 'var(--background-popout)',
  backgroundSubtle: 'var(--background-subtle)',
  backgroundBoxed: 'var(--background-boxed)',
  backgroundBoxedHover: 'var(--background-boxed-hover)',
  backgroundOk: 'var(--background-ok)',
  backgroundWarning: 'var(--background-warning)',
  backgroundWarningHover: 'var(--background-warning-hover)',
  backgroundPrimary: 'var(--background-primary)',
  backgroundPrimaryHover: 'var(--background-primary-hover)',
  backgroundSecondaryHover: 'var(--background-secondary-hover)',
  backgroundFocus: 'var(--background-focus)',
  backgroundAccent: 'var(--background-accent)',
  backgroundAccentHover: 'var(--background-accent-hover)',
  lineDefault: 'var(--line-default)',
  lineInput: 'var(--line-input)',
  lineInputHover: 'var(--line-input-hover)',
  lineFocus: 'var(--line-focus)',
  lineImportant: 'var(--line-important)',
  lineImportantHover: 'var(--line-important-hover)',
  textDefault: 'var(--text-default)',
  textDimmed: 'var(--text-dimmed)',
  textDeselected: 'var(--text-deselected)',
  textDisabled: 'var(--text-disabled)',
  textPrimary: 'var(--text-primary)',
  textImportantDimmed: 'var(--text-important-dimmed)',
  textImportant: 'var(--text-important)',
  textDanger: 'var(--text-danger)',
  textLogo: 'var(--text-logo)',
} as const satisfies Record<keyof ThemeDefinition, string>

const generateThemeCssVariables = (theme: ThemeDefinition) => {
  return `
    --background-default: ${theme.backgroundDefault};
    --background-popout: ${theme.backgroundPopout};
    --background-subtle: ${theme.backgroundSubtle};
    --background-focus: ${theme.backgroundFocus};
    --background-boxed: ${theme.backgroundBoxed};
    --background-boxed-hover: ${theme.backgroundBoxedHover};
    --background-ok: ${theme.backgroundOk};
    --background-warning: ${theme.backgroundWarning};
    --background-warning-hover: ${theme.backgroundWarningHover};
    --background-primary: ${theme.backgroundPrimary};
    --background-primary-hover: ${theme.backgroundPrimaryHover};
    --background-secondary-hover: ${theme.backgroundSecondaryHover};
    --background-accent: ${theme.backgroundAccent};
    --background-accent-hover: ${theme.backgroundAccentHover};
    --line-default: ${theme.lineDefault};
    --line-input: ${theme.lineInput};
    --line-input-hover: ${theme.lineInputHover};
    --line-focus: ${theme.lineFocus};
    --line-important: ${theme.lineImportant};
    --line-important-hover: ${theme.lineImportantHover};
    --text-default: ${theme.textDefault};
    --text-dimmed: ${theme.textDimmed};
    --text-deselected: ${theme.textDeselected};
    --text-disabled: ${theme.textDisabled};
    --text-primary: ${theme.textPrimary};
    --text-important: ${theme.textImportant};
    --text-important-dimmed: ${theme.textImportantDimmed};
    --text-danger: ${theme.textDanger};
    --text-logo: ${theme.textLogo}` as const
}

// this injects the css variables into the global scope automatically
export const themeCss = css`
  :global() {
    body,
    body[data-theme='default'] {
      ${generateThemeCssVariables(defaultTheme)}
    }

    body[data-theme='dark'] {
      ${generateThemeCssVariables(darkTheme)}
    }

    body[data-theme='high-contrast'] {
      ${generateThemeCssVariables(highContrastTheme)}
    }
  }
`

// css`
//   // usage
//
//   .my-page {
//     background-color: ${themeColors.background};
//     color: ${themeColors.textColor};
//   }
//
//   .my-filter-header {
//     background-color: ${themeColors.emphasizedBackground};
//     color: ${themeColors.emphasizedText};
//   }
// `
